import { useEffect, useState } from "react";
import * as d3 from "d3";
import { parse } from "date-fns";
import type { ChartData, ChartOptions } from "chart.js";
import useWindowSize from "@/hooks/useWindowSize";

type UseChartDataParams = {
  url: string;
  name: string;
  min?: number;
  max?: number;
};

type UseChartDataReturn = {
  prices: number[];
  dates: Date[];
  showChart: boolean;
  data: ChartData<"line">;
  baseOptions: ChartOptions<"line">;
};

export function useChartData({
  url,
  name,
  min,
  max,
}: UseChartDataParams): UseChartDataReturn {
  const [prices, setPrices] = useState<number[]>([]);
  const [dates, setDates] = useState<Date[]>([]);
  const [showChart, setShowChart] = useState(true);

  const { width } = useWindowSize();

  useEffect(() => {
    if (typeof window !== "undefined") {
      d3.csv(url)
        .then((res) => {
          const prices: number[] = [];
          const dates: Date[] = [];

          for (const row of res) {
            const price = Number(row["price-" + name.trim().toLowerCase()]);
            if (isNaN(price) || price === 0) continue;

            const date = parse(row.date ?? "", "dd/MM/yyyy", new Date());
            prices.push(price);
            dates.push(date);
          }

          setPrices(prices);
          setDates(dates);
        })
        .catch((err) => console.error("FETCHING ERROR: ", err));
    }
  }, [url, name]);

  useEffect(() => {
    setShowChart(false);
    setTimeout(() => setShowChart(true), 100);
  }, [width]);

  const data: ChartData<"line"> = {
    labels: dates,
    datasets: [
      {
        data: prices,
        backgroundColor: "rgba(29, 112, 183, 0.5)",
        borderColor: "#1d70b7",
        borderWidth: 1.5,
        fill: false,
        tension: 0,
      },
    ],
  };

  const baseOptions: ChartOptions<"line"> = {
    plugins: {
      legend: {
        display: false,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
        min: min ?? undefined,
        max: max ?? undefined,
      },
    },
    events: [],
  };

  return { prices, dates, showChart, data, baseOptions };
}
